import { z } from 'zod';
/**
 * Canonical headcount vocabulary (headcount planning work package A1).
 *
 * Single source of truth for divisions, departments, and role grades across
 * the hiring, forecast, and (future) position-ledger surfaces. Four legacy
 * vocabularies map onto this one:
 *
 * 1. Hiring spots / placements: the 4-value HiringRole enum
 *    (apps/web/src/shared/types/hiringSpot.ts) — a strict subset of RoleGrade.
 * 2. BQ hiring forecast strings (hiring_forecasts docs): entry = division,
 *    entry_category = department, entry_subcategory = grade, plus 'Unknown'
 *    rows for unclassified headcount.
 * 3. Free-form HR role names (user_pivot_sync / system_users role_name,
 *    e.g. "Associate Director, Strategy") — resolved to an exact RoleGrade
 *    by parseRoleGradeFromRoleName for headcount pricing (A5a).
 * 4. Finance pipeline pipe-delimited role keys "Division|Department|Grade"
 *    (StrategicConfig.salaries, role_distributions) — the pipeline labels the
 *    segments "Department|Sub-Department|Grade", but its "Department" is the
 *    canonical division and its "Sub-Department" is the canonical department.
 *
 * Distinct from the RoleCategory classifier
 * (apps/web/src/shared/classifiers/roles/), which buckets free-form role_name
 * strings into coarse seniority tiers. RoleGrade is the exact planning
 * vocabulary; RoleCategory is a display/grouping heuristic.
 *
 * Decision (Ross, 2026-07-11): all 7 grades are canonical and Executive /
 * Director positions flow through the same position lifecycle and gates as
 * every other grade.
 */
// ---------------------------------------------------------------------------
// Divisions
// ---------------------------------------------------------------------------
export const DIVISIONS = ['Projects', 'Operational'];
export const DivisionSchema = z.enum(DIVISIONS);
// ---------------------------------------------------------------------------
// Departments (canonical seven)
// ---------------------------------------------------------------------------
export const DEPARTMENTS = [
    'Performance',
    'Creative',
    'Data',
    'Business Development',
    'Business Operations',
    'Leadership',
    'People & Culture',
];
export const DepartmentSchema = z.enum(DEPARTMENTS);
export const DEPARTMENT_DIVISION = {
    Performance: 'Projects',
    Creative: 'Projects',
    Data: 'Projects',
    'Business Development': 'Operational',
    'Business Operations': 'Operational',
    Leadership: 'Operational',
    'People & Culture': 'Operational',
};
// ---------------------------------------------------------------------------
// Role grades (canonical seven), ascending seniority
// ---------------------------------------------------------------------------
/**
 * Ordered ascending by seniority, consistent with the intel seniority buckets
 * (Lead & Specialist / Manager & Strategist / Associate Director +).
 */
export const ROLE_GRADES = [
    'Specialist',
    'Lead',
    'Strategist',
    'Manager',
    'Associate Director',
    'Director',
    'Executive',
];
export const RoleGradeSchema = z.enum(ROLE_GRADES);
/** Ascending seniority rank (Specialist = 0 … Executive = 6). For sorting. */
export function roleGradeRank(grade) {
    return ROLE_GRADES.indexOf(grade);
}
// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------
export function isDivision(value) {
    return typeof value === 'string' && DIVISIONS.includes(value);
}
export function isDepartment(value) {
    return typeof value === 'string' && DEPARTMENTS.includes(value);
}
export function isRoleGrade(value) {
    return typeof value === 'string' && ROLE_GRADES.includes(value);
}
// ---------------------------------------------------------------------------
// Mapping 1: HiringRole spot enum → RoleGrade
// ---------------------------------------------------------------------------
/**
 * The 4 grades hiring spots / placements can carry today. String-identical to
 * their canonical RoleGrade values; kept as an explicit map so a future spot
 * enum rename cannot silently diverge from the canonical vocabulary.
 */
export const HIRING_ROLE_TO_ROLE_GRADE = {
    Specialist: 'Specialist',
    Lead: 'Lead',
    Strategist: 'Strategist',
    Manager: 'Manager',
};
export function mapHiringRoleToRoleGrade(role) {
    if (!role)
        return null;
    return HIRING_ROLE_TO_ROLE_GRADE[role.trim()] ?? null;
}
// ---------------------------------------------------------------------------
// Mapping 2: BQ string vocabulary → canonical enums
// ---------------------------------------------------------------------------
function matchIgnoringCase(value, candidates) {
    if (!value)
        return null;
    const normalized = value.trim().toLowerCase();
    if (normalized.length === 0)
        return null;
    return candidates.find((c) => c.toLowerCase() === normalized) ?? null;
}
/**
 * Parse a BQ division string (`entry` on Headcount rows). 'Unknown' and
 * unrecognized values return null.
 */
export function parseDivision(value) {
    return matchIgnoringCase(value, DIVISIONS);
}
/**
 * Parse a BQ department string (`entry_category` on Headcount rows).
 * 'Unknown' and unrecognized values return null.
 */
export function parseDepartment(value) {
    return matchIgnoringCase(value, DEPARTMENTS);
}
/**
 * Parse a BQ grade string (`entry_subcategory` on Headcount rows).
 * 'Unknown' and unrecognized values return null.
 */
export function parseRoleGrade(value) {
    return matchIgnoringCase(value, ROLE_GRADES);
}
// ---------------------------------------------------------------------------
// Mapping 3: free-form HR role names → RoleGrade
// ---------------------------------------------------------------------------
/**
 * Parse a free-form role_name string (user_pivot_sync / system_users, e.g.
 * "Performance Marketing Specialist", "Associate Director, Strategy") into a
 * canonical RoleGrade.
 *
 * Distinct from the classifyRole display heuristic
 * (apps/web/src/shared/classifiers/roles/), which buckets into coarse
 * RoleCategory tiers and collapses Associate Director into 'director'. This
 * parser returns the exact planning grade for headcount math (rate card
 * pricing, program attribution — work package A5a) and null when the role
 * cannot be resolved, so callers can surface unresolvable people instead of
 * silently mispricing them.
 *
 * Matching is case-insensitive, senior-first, so compound titles resolve to
 * the most senior grade they mention ("Managing Director, Strategy" →
 * Director, "Performance Manager, Data Operations" → Manager).
 */
export function parseRoleGradeFromRoleName(roleName) {
    if (!roleName)
        return null;
    const normalized = roleName.trim().toLowerCase();
    if (normalized.length === 0)
        return null;
    if (/\b(ceo|cto|cfo|coo|chief|vp|vice president|president|founder|executive)\b/.test(normalized)) {
        return 'Executive';
    }
    if (normalized.includes('associate director'))
        return 'Associate Director';
    if (normalized.includes('director'))
        return 'Director';
    if (normalized.includes('manager'))
        return 'Manager';
    if (normalized.includes('strategist'))
        return 'Strategist';
    if (normalized.includes('lead'))
        return 'Lead';
    if (normalized.includes('specialist'))
        return 'Specialist';
    return null;
}
/**
 * Parse a pipe-delimited role key (e.g. "Projects|Performance|Lead") into
 * canonical enum values. Returns null if any segment fails to resolve —
 * including the "Unknown" fallback key used by the salary table.
 */
export function parseCanonicalRoleKey(key) {
    if (!key)
        return null;
    const [rawDivision, rawDepartment, rawGrade] = key.split('|');
    const division = parseDivision(rawDivision);
    const department = parseDepartment(rawDepartment);
    const grade = parseRoleGrade(rawGrade);
    if (!division || !department || !grade)
        return null;
    if (DEPARTMENT_DIVISION[department] !== division)
        return null;
    return { division, department, grade };
}
/**
 * Build the canonical pipe-delimited role key for a department/grade pair.
 * The division segment is derived — callers cannot mis-file a department.
 */
export function buildRoleKey(department, grade) {
    return `${DEPARTMENT_DIVISION[department]}|${department}|${grade}`;
}
